import { useState, useEffect } from "react";
import {
  FileText,
  Search,
  X,
  Clock,
  TrendingUp,
  Filter,
  Eye,
  ChevronRight,
} from "lucide-react";
import medicalDB from "../services/database";

const DraftSelector = ({ onSelectDraft, onClose }) => {
  const [drafts, setDrafts] = useState([]);
  const [filteredDrafts, setFilteredDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [viewMode, setViewMode] = useState("popular"); // 'popular', 'recent', 'all'

  const categories = [
    "General",
    "Preventive Care",
    "Acute Care",
    "Chronic Care",
    "Emergency",
    "Specialty",
  ];

  useEffect(() => {
    loadDrafts();
  }, [viewMode]);

  useEffect(() => {
    filterDrafts();
  }, [drafts, searchTerm, selectedCategory]);

  const loadDrafts = async () => {
    try {
      setLoading(true);
      let draftData;

      switch (viewMode) {
        case "popular":
          draftData = await medicalDB.getPopularDrafts(20);
          break;
        case "recent":
          draftData = await medicalDB.getRecentDrafts(20);
          break;
        case "all":
        default:
          draftData = await medicalDB.getAllDrafts();
          break;
      }

      setDrafts(draftData);
    } catch (error) {
      console.error("Error loading drafts:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterDrafts = () => {
    let filtered = [...drafts];

    if (searchTerm) {
      filtered = filtered.filter(
        (draft) =>
          draft.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          draft.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(
        (draft) => draft.category === selectedCategory
      );
    }

    setFilteredDrafts(filtered);
  };

  const handleSelectDraft = async (draft) => {
    try {
      // Increment usage count
      await medicalDB.incrementDraftUsage(draft.id);
      onSelectDraft(draft);
    } catch (error) {
      console.error("Error updating draft usage:", error);
      // Still proceed with selection even if usage tracking fails
      onSelectDraft(draft);
    }
  };

  const formatUsageInfo = (draft) => {
    const usageCount = draft.usageCount || 0;
    if (usageCount === 0) return "Never used";
    if (usageCount === 1) return "Used once";
    return `Used ${usageCount} times`;
  };

  const getViewModeLabel = () => {
    switch (viewMode) {
      case "popular":
        return "Most Popular";
      case "recent":
        return "Recently Updated";
      case "all":
        return "All Templates";
      default:
        return "Templates";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-600">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <FileText className="h-6 w-6 text-medical-blue" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Choose Visit Template
            </h3>
            <p className="text-sm text-gray-600">
              Select a template to pre-fill the visit form
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 p-2"
        >
          <X size={20} />
        </button>
      </div>

      {/* View Mode Tabs */}
      <div className="flex items-center space-x-1 mb-4 bg-gray-100 rounded-lg p-1">
        <button
          onClick={() => setViewMode("popular")}
          className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            viewMode === "popular"
              ? "bg-white text-medical-blue shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <TrendingUp size={16} />
          <span>Popular</span>
        </button>
        <button
          onClick={() => setViewMode("recent")}
          className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            viewMode === "recent"
              ? "bg-white text-medical-blue shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <Clock size={16} />
          <span>Recent</span>
        </button>
        <button
          onClick={() => setViewMode("all")}
          className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            viewMode === "all"
              ? "bg-white text-medical-blue shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <FileText size={16} />
          <span>All</span>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 form-input"
          />
        </div>
        <div className="relative">
          <Filter
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={18}
          />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="pl-10 form-input"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-600 mb-4">
        {filteredDrafts.length > 0 ? (
          <span>
            Showing {filteredDrafts.length} {getViewModeLabel().toLowerCase()}{" "}
            {filteredDrafts.length === 1 ? "template" : "templates"}
          </span>
        ) : (
          <span>No templates found</span>
        )}
      </div>

      {/* Templates List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {filteredDrafts.map((draft) => (
          <div
            key={draft.id}
            className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={() => handleSelectDraft(draft)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h4 className="font-medium text-gray-900">{draft.name}</h4>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-medical-lightblue text-medical-blue">
                    {draft.category}
                  </span>
                </div>

                {draft.description && (
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {draft.description}
                  </p>
                )}

                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{formatUsageInfo(draft)}</span>
                  <div className="flex items-center space-x-3">
                    {draft.template.complaints?.length > 0 && (
                      <span>{draft.template.complaints.length} complaints</span>
                    )}
                    {draft.template.prescription?.length > 0 && (
                      <span>
                        {draft.template.prescription.length} prescriptions
                      </span>
                    )}
                    {draft.template.takenDrugs?.length > 0 && (
                      <span>
                        {draft.template.takenDrugs.length} medications
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <ChevronRight
                size={20}
                className="text-gray-400 ml-3 flex-shrink-0"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredDrafts.length === 0 && !loading && (
        <div className="text-center py-8">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">
            No templates found
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {searchTerm || selectedCategory
              ? "Try adjusting your search or filters"
              : "No templates available yet"}
          </p>
          {!searchTerm && !selectedCategory && (
            <p className="text-xs text-gray-400">
              Templates can be created from the Dashboard or by saving a visit
              as a template
            </p>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Select a template to pre-fill the form fields</span>
          <button
            onClick={onClose}
            className="text-medical-blue hover:text-blue-700 font-medium"
          >
            Skip Templates
          </button>
        </div>
      </div>
    </div>
  );
};

export default DraftSelector;
